/**
 * Collection Media Helper Utilities
 * Extracts image URLs, determines regeneration availability, status display
 */

import type { Collection, MediaStatus } from '../types/collections.types';

const isDev = import.meta.env.DEV;

/**
 * Extract image URLs from collection's products
 * Prioritizes transformed_products over products_list
 */
export function extractImageUrls(collection: Collection): string[] {
  try {
    // PRIORITY 1: transformed_products (already formatted)
    if (collection.transformed_products?.length) {
      return collection.transformed_products
        .map((p) => p.image)
        .filter(Boolean) as string[];
    }

    // PRIORITY 2: products_list (may need parsing)
    let list: any = collection.products_list;
    if (typeof list === 'string') { try { list = JSON.parse(list); } catch { return []; } }
    if (typeof list === 'string') { try { list = JSON.parse(list); } catch { return []; } }
    if (Array.isArray(list) && list.length === 1 && Array.isArray(list[0])) list = list[0];

    if (Array.isArray(list)) {
      return list
        .map((p: any) => {
          if (typeof p === 'string') {
            try { return JSON.parse(p).image; } catch { return null; }
          }
          return p?.image || p?.imageUrl;
        })
        .filter(Boolean) as string[];
    }

    return [];
  } catch {
    if (isDev) console.error('❌ extractImageUrls failed');
    return [];
  }
}

/** Can regenerate image: completed, failed, or no status */
export function canRegenerateImage(status?: MediaStatus | null): boolean {
  return !status || status === 'completed' || status === 'failed';
}

/** Can regenerate video: completed, failed, or no status */
export function canRegenerateVideo(status?: MediaStatus | null): boolean {
  return !status || status === 'completed' || status === 'failed';
}

/** User-friendly status text */
export function getStatusText(status?: MediaStatus | null): string {
  switch (status) {
    case 'pending': return 'Queued...';
    case 'processing': return 'Generating...';
    case 'completed': return 'Ready';
    case 'failed': return 'Failed';
    default: return 'Not Generated';
  }
}

/** Tailwind status color class */
export function getStatusColor(status?: MediaStatus | null): string {
  switch (status) {
    case 'pending': return 'text-yellow-500';
    case 'processing': return 'text-blue-500';
    case 'completed': return 'text-green-500';
    case 'failed': return 'text-red-500';
    default: return 'text-gray-400';
  }
}
